import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'
import Paper from '@material-ui/core/Paper'
import Box from '@material-ui/core/Box'
import Grid from '@material-ui/core/Grid'
import Typography from '@material-ui/core/Typography'
import { DateInputValue } from '../../../components/default/form/DateInput'
import LoadingAndErrorFrame from '../../../components/default/frames/LoadingAndErrorFrame'
import FormStateProvider from '../../../components/default/form/FormStateProvider'
import useGDK from '../../../providers/admin/gdk/useGDK'
import useT from '../../../i18ns/admin/useT'
import { getValueFromChangeEvent, getValueFromValue, getTimeFromDateInputValue } from '../../../utils/default/FormHelper'
import { usePageFlow } from '../../../utils/default/PageFlowHook'
import { useDialogHandleSubmit, useRedirectHandleBack } from '../../../utils/default/ComplexFlowHook'
import { createDefaultFormState, FormValidation, ValueGetter } from '../../../utils/default/FormHook'
import useGlobalDialog from '../../../providers/admin/dialog/useGlobalDialog'
import { createGlobalDialogConfig } from '../../../utils/default/DialogHelper'
import { ActivityWalletLimitType, GameCategoryType, GameType, ActivityWalletCsvRes, ActivityWalletUserRes, GDKError } from '@golden/gdk-admin'
import { createValidateNotEmpty, createValidateDropDownNotEmpty, createCorrectResult, createErrorResult } from '../../../utils/default/Validator'
import { Path } from '../../../components/admin/route/route'
import { BcMath } from '@golden/bcmath'
import getUnixTime from 'date-fns/getUnixTime'
import { useCommonStyles } from '../../../utils/admin/StyleHook'
import ActivityWalletCreateStep1 from '../../../components/admin/activity/wallet/ActivityWalletCreateStep1'
import { Step2Csv, Step2User } from '../../../components/admin/activity/wallet/ActivityWalletCreateStep2'

export type FormType = {
  account: string
  userId: number | null
  amount: number | string
  file: File | null
  activityWalletTitleId: number | null
  multiple: string
  expired_at: DateInputValue
  code: string
  activityContent: string
  bindingDeposits: string
  } & (
    {
    limit_type: ActivityWalletLimitType.GAME_CATEGORY, limit_ids: GameCategoryType[]
    } | {
    limit_type: ActivityWalletLimitType.GAME, limit_ids: GameType[]
    }
  )
export const defaultValue: FormType = {
  account: '',
  userId: null,
  file: null,
  amount: '',
  activityWalletTitleId: null,
  multiple: '3',
  expired_at: {
    start: null,
    end: null
  },
  limit_type: ActivityWalletLimitType.GAME_CATEGORY as ActivityWalletLimitType,
  limit_ids: [],
  code: '',
  activityContent: '',
  bindingDeposits: ''
}

const getValueFromEvent: ValueGetter<FormType> = {
  account: getValueFromValue,
  userId: getValueFromChangeEvent,
  file: getValueFromValue,
  amount: getValueFromChangeEvent,
  activityWalletTitleId: getValueFromChangeEvent,
  multiple: getValueFromChangeEvent,
  expired_at: getTimeFromDateInputValue,
  limit_type: getValueFromChangeEvent,
  limit_ids: getValueFromChangeEvent,
  code: getValueFromChangeEvent,
  activityContent: getValueFromChangeEvent,
  bindingDeposits: getValueFromChangeEvent
}

export const FormContext = createContext(createDefaultFormState(defaultValue))

const ActivityWalletCreatePage: React.FC = () => {
  const gdk = useGDK()
  const { t } = useT()
  const pageFlow = usePageFlow(false, false)
  const commonClasses = useCommonStyles()

  const validation: FormValidation<FormType> = useMemo(() => {
    const minAmount = 8
    return {
      account: [],
      userId: [],
      file: [],
      amount: [
        {
          func: (value: unknown, form: FormType) => {
            if (form.file) return createCorrectResult('amount')
            if(Number(value) === 0) return createErrorResult('amount', t('error.mustNotEmpty'))
            if (Number(value) < minAmount) return createErrorResult('amount', `${t('common.notOKShort')} < ${minAmount}`)
            return createCorrectResult('amount')
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      activityWalletTitleId: [
        {
          func: createValidateNotEmpty('activityWalletTitleId', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      multiple: [
        {
          func: (value: unknown) => {
            if (Number(value) === 0) return createErrorResult('multiple', t('error.mustNotZero'))
            if (!Number.isInteger(Number(value))) return createErrorResult('multiple', t('error.mustBeInt'))
            return createCorrectResult('multiple')
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      expired_at: [
        {
          func: (value: unknown) => {
            if (!(value as DateInputValue).end) return createErrorResult('expired_at', t('error.mustNotEmpty'))
            return createCorrectResult('expired_at')
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      limit_type: [],
      limit_ids: [
        {
          func: (value: unknown) => {
            if ((value as string[])?.length) return createCorrectResult('limit_ids')
            return createErrorResult('limit_ids', '')
          },
          when: ['change', 'beforeClickSubmit']
        }
      ],
      code: [
        {
          func: createValidateNotEmpty('code', t),
          when: ['change', 'beforeClickSubmit']
        }
      ],
      activityContent: [],
      bindingDeposits: []
    }
  }, [t])

  const globalDialog = useGlobalDialog()

  const [resCsv, setResCsv] = useState<ActivityWalletCsvRes | null>(null)
  const [resUser, setResUser] = useState<ActivityWalletUserRes | null>(null)
  const [options, setOptions] = useState<Array<{ value: number, name: string }>>([])

  const formToRequestCsv = (form: FormType) => {
    return ({
      file: form.file as File,
      id: form.activityWalletTitleId,
      multiple: Number(form.multiple),
      expired_at: form.expired_at.end,
      limit_type: form.limit_type,
      limit_ids: form.limit_ids,
      code: form.code
    }) as {
      file: File
      id: number
      multiple: number
      expired_at: Date
      code: string
    } & ({ limit_type: ActivityWalletLimitType.GAME_CATEGORY, limit_ids: GameCategoryType[] } | { limit_type: ActivityWalletLimitType.GAME, limit_ids: GameType[] })
  }

  const formToRequestUser = (form: FormType) => {
    return ({
      user_id: resUser?.id as number,
      amount: Number(form.amount),
      activity_wallet_title_id: form.activityWalletTitleId as number,
      multiple: Number(form.multiple),
      expired_at: getUnixTime(form.expired_at.end as Date),
      limit_type: form.limit_type,
      limit_ids: form.limit_ids,
      activity_content: form.activityContent,
      binding_deposits: form.bindingDeposits,
      code: form.code
    })
  }

  const [handleBack] = useRedirectHandleBack({
    path: Path.ACTIVITY_WALLET
  })
  const { handleSubmit: csvSubmit } = useDialogHandleSubmit({
    formToRequest: formToRequestCsv,
    globalDialog,
    dialogId: 'createActivityWallet',
    gdkFunc: (payload) => gdk.activity.postActivityWalletCreateCsv(payload),
    gdkFuncDependencies: [gdk],
    getChangeDialogConfig: (form) => {
      const count = resCsv?.data.length ?? 0
      const title = options.find((item) => item.value === form.activityWalletTitleId)?.name ?? ''
      const cash = resCsv?.data.reduce((accu, curr) => BcMath.base(accu).add(Number(curr.amount)).get(), '0.0000')
      return createGlobalDialogConfig({
        showIcon: false,
        message: t('dialog.confirmCreateActivityWallet', { count, title, cash })
      })
    },
    getSuccessDialogConfig: () => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.sendSuccess3')
    }),
    getFailDialogConfig: (_, error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error.message
    }),
    afterSuccessDialog: handleBack
  })

  const { handleSubmit: userSubmit } = useDialogHandleSubmit({
    formToRequest: formToRequestUser,
    globalDialog,
    dialogId: 'createUserActivityWallet',
    gdkFunc: (payload) => gdk.activity.postActivityWalletCreate(payload),
    gdkFuncDependencies: [gdk],
    getChangeDialogConfig: (form) => {
      const title = options.find((item) => item.value === form.activityWalletTitleId)?.name ?? ''
      return createGlobalDialogConfig({
        showIcon: false,
        message: t('dialog.confirmCreateActivityWallet', { count: 1, title, cash: form.amount })
      })
    },
    getSuccessDialogConfig: () => createGlobalDialogConfig({
      showIcon: false,
      showCancel: false,
      message: t('dialog.sendSuccess3')
    }),
    getFailDialogConfig: (_, error) => createGlobalDialogConfig({
      showIcon: true,
      variant: 'error',
      showCancel: false,
      message: error.message
    }),
    afterSuccessDialog: handleBack
  })

  const handleSubmit = useCallback((form: FormType) => {
    if (form.file) csvSubmit(form)
    if (form.account) userSubmit(form)
      return form
  }, [csvSubmit, userSubmit])

  return (
    <Box padding={5}>
      <FormStateProvider<FormType>
        context={FormContext}
        defaultValue={defaultValue}
        onSubmit={handleSubmit}
        getValueFromEvent={getValueFromEvent}
        validation={validation}
      >
        <Paper>
          <Box padding={4}>
            <Grid container direction="column" spacing={6}>
              <ActivityWalletCreateStep1 setResUser={setResUser} setResCsv={setResCsv} pageFlow={pageFlow} />
            </Grid>
          </Box>
        </Paper>
        {
          (pageFlow.status.error || pageFlow.status.loading || pageFlow.status.showContent) && (
            <Box marginTop={2}>
              <Paper>
                <Box padding={4}>
                  <Grid item xs={12}>
                    <LoadingAndErrorFrame {...pageFlow.status}>
                      <Grid container direction="column" spacing={6}>
                        <Grid item xs={12}>
                          {
                            (resCsv || resUser) && (
                              <Box
                                paddingY={1.25}
                                paddingX={2}
                                className={commonClasses.pinkTitleBar}
                              >
                                <Typography variant="h5">
                                  {t('page.activityWalletCreate')}
                                </Typography>
                              </Box>
                            )
                          }
                        </Grid>
                        <Step2Csv pageFlow={pageFlow} resCsv={resCsv} options={options} setOptions={setOptions} />
                        <Step2User pageFlow={pageFlow} resUser={resUser} options={options} setOptions={setOptions} />
                      </Grid>
                    </LoadingAndErrorFrame>
                  </Grid>
                </Box>
              </Paper>
            </Box>
          )
        }
      </FormStateProvider>
    </Box>
  )
}

export default React.memo(ActivityWalletCreatePage)